import { ComplexField } from "./ComplexField";
import AppSpinner from "../../../elements/AppSpinner";
import DisplayRecord from "../DisplayRecord";
import { Popover, Whisper } from "rsuite";
import { forwardRef, Ref, useImperativeHandle, useRef } from "react";
import EmptyField from "../EmptyField";
import AppTooltip from "../../../elements/AppTooltip";

interface OnUpdateObject {
  fieldAPIName: string;
  recordID: string;
  payload: any;
  subitemParent: string;
}

interface Props {
  open: boolean;
  mode: string;
  tableView: boolean;
  isEditing: boolean;
  setOpen: (val: any) => void;
  values: any;
  existingValues: any;
  onUpdate: (object: OnUpdateObject) => void;
  field: any;
  recordID: string;
  setExistingValues: (val: any) => void;
  setIsEditing: (val: boolean) => void;
  setValues: (values: any) => void;
  modifyTrigger: any;
  submitting: boolean;
  type: string;
}

const ComplexRecordDrawer = (
  {
    open,
    mode,
    tableView,
    isEditing,
    setOpen,
    values,
    existingValues,
    onUpdate,
    field,
    recordID,
    setExistingValues,
    setIsEditing,
    setValues,
    modifyTrigger,
    submitting,
    type,
  }: Props,
  ref: Ref<HTMLInputElement>
) => {
  const whisperRef: any = useRef();

  const handleOpenModify = () => {
    setOpen((prev: boolean) => !prev);
    whisperRef?.current?.open();
  };

  const handleCloseModify = () => {
    whisperRef?.current?.close();
  };

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    focus: () => {
      handleOpenModify();
    },
  }));

  return (
    <Whisper
      preventOverflow
      ref={whisperRef}
      placement="autoVerticalStart"
      trigger={isEditing ? "none" : "click"}
      onClose={() => {
        if (isEditing) return;

        if (JSON.stringify(values) === JSON.stringify(existingValues)) {
          return;
        }

        onUpdate({
          fieldAPIName: field?.APIName,
          recordID: recordID,
          payload: values,
          subitemParent: field?.SubitemParent,
        });

        setExistingValues(structuredClone(values));
        handleCloseModify();
      }}
      speaker={
        <Popover
          style={{
            boxShadow: "0 1px 8px rgba(0,0,0,0.12)",
            filter: "drop-shadow(var(--rs-popover-shadow))",
          }}
          className="p-0"
          arrow={false}
        >
          <ComplexField
            recordID={recordID}
            field={field}
            onUpdate={(payload: any) => {
              onUpdate(payload);
              setExistingValues(payload.payload);
            }}
            closeRecord={() => {
              setOpen(false);
              setIsEditing(false);
              whisperRef.current.close();
            }}
            existingValues={existingValues}
            values={values}
            setValues={setValues}
            setIsEditing={setIsEditing}
            /* @ts-ignore */
            type={type}
          />
        </Popover>
      }
    >
      <div>
        <DisplayRecord
          mode={mode as "r" | "rw"}
          tableView={tableView}
          modifyTrigger={modifyTrigger}
          openModify={() => handleOpenModify()}
          isSensitive={field?.Sensitive ?? false}
          displayComponent={(_: any, innerRef: any) => {
            if (submitting) return <AppSpinner size="sm" />;

            return (
              <div
                className="text-truncate"
                style={{ maxWidth: 350 }}
                ref={innerRef}
              >
                {typeof existingValues !== "string" &&
                existingValues?.length > 0 ? (
                  <>
                    {tableView
                      ? existingValues.map((val: any, index: number) => {
                          if (type === "URL") {
                            return (
                              <AppTooltip
                                placement="autoHorizontalEnd"
                                useTooltip
                                content="Open In New Tab"
                              >
                                <a
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(
                                      val[type].includes("http")
                                        ? val[type]
                                        : `https://${val[type]}`,
                                      "_blank"
                                    );
                                  }}
                                  target="_blank"
                                  className="primary"
                                >
                                  {val[type]}
                                  {index + 1 !== existingValues.length
                                    ? ", "
                                    : ""}
                                </a>
                              </AppTooltip>
                            );
                          }
                          return `${val[type]}${
                            index + 1 !== existingValues.length ? ", " : ""
                          }`;
                        })
                      : existingValues.map((val: any) => {
                          return (
                            <span key={val.id} className="tw:truncate tw:block">
                              {val[type]}
                            </span>
                          );
                        })}
                  </>
                ) : (
                  <EmptyField table={tableView} />
                )}
              </div>
            );
          }}
        />
      </div>
    </Whisper>
  );
};

export default forwardRef(ComplexRecordDrawer);
